import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { schoolAPI } from '../services/api';
import './Landing.css';

const Landing = () => {
  const navigate = useNavigate();
  const { language, toggleLanguage, t } = useLanguage();
  const { login, signup, isAuthenticated, user } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [selectedRole, setSelectedRole] = useState('student');
  const [scrolled, setScrolled] = useState(false);
  const [error, setError] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    birthDate: '',
    age: '',
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const routes = {
        student: '/student',
        teacher: '/teacher',
        admin: '/admin',
        school: '/school',
      };
      navigate(routes[user.role] || '/');
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        // Login - single endpoint auto-detects user vs school
        const response = await login({
          username: formData.username,
          password: formData.password,
        });
        // If backend returned a school account, redirect to school portal
        if (response._isSchool) {
          navigate('/school');
          return;
        }
        // Regular user — navigation happens automatically via useEffect
      } else {
        // Signup - validate passwords match
        if (formData.password !== formData.confirmPassword) {
          setError(language === 'zh' ? '密码不匹配' : 'Passwords do not match');
          setLoading(false);
          return;
        }

        await signup({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          role: selectedRole,
          age: selectedRole === 'student' && formData.age ? parseInt(formData.age) : null,
        });
      }

      // Navigation will happen automatically via useEffect
    } catch (err) {
      setError(err.message || (language === 'zh' ? '操作失败，请重试' : 'Operation failed, please try again'));
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing-dark">
      {/* Navbar */}
      <nav className={`navbar-dark ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <div className="nav-logo">
            <span className="logo-icon">&#9670;</span>
            <span className="logo-text">MESAMIND</span>
          </div>

          <div className="nav-links">
            <button onClick={() => scrollToSection('features')}>{t.nav.features}</button>
            <button onClick={() => scrollToSection('about')}>{t.nav.about}</button>
            <button onClick={() => scrollToSection('pricing')}>{t.nav.pricing}</button>
          </div>

          <div className="nav-actions">
            <button className="lang-toggle" onClick={toggleLanguage}>
              {language === 'zh' ? 'EN' : '中文'}
            </button>
            <button className="btn-primary-nav" onClick={() => navigate('/canvas')}>
              {t.nav.tryDemo}
            </button>
          </div>

          {/* Mobile Menu */}
          <div className="mobile-actions">
            <button className="lang-toggle-mobile" onClick={toggleLanguage}>
              {language === 'zh' ? 'EN' : '中'}
            </button>
            <button className="btn-primary-mobile" onClick={() => scrollToSection('auth')}>
              {t.nav.login}
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-dark" id="auth">
        <div className="hero-bg">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>

        <div className="hero-content">
          <div className="hero-badge">{t.hero.badge}</div>
          <h1 className="hero-title">
            {t.hero.title}
            <span className="title-highlight">{t.hero.titleHighlight}</span>
          </h1>
          <p className="hero-subtitle">{t.hero.subtitle}</p>

          <div className="hero-buttons">
            <button className="btn-hero-primary" onClick={() => navigate('/canvas')}>
              <span>{t.nav.tryDemo}</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
            <button className="btn-hero-secondary" onClick={() => scrollToSection('features')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="5 3 19 12 5 21 5 3"/>
              </svg>
              <span>{t.hero.watchDemo}</span>
            </button>
          </div>

          <div className="hero-trust">
            <div className="trust-avatars">
              <div className="avatar">&#127891;</div>
              <div className="avatar">&#128104;&#8205;&#127979;</div>
              <div className="avatar">&#128105;&#8205;&#127912;</div>
              <div className="avatar">&#129489;&#8205;&#128187;</div>
            </div>
            <p>{t.hero.trustedBy} <strong>{language === 'zh' ? '8年、300+校次' : '8 Years, 300+ School Sessions'}</strong> {t.hero.schools}</p>
          </div>
        </div>

        {/* Auth Form - Horizontal Layout */}
        <div className="hero-visual">
          <div className="auth-container">
            {/* Left Column - Branding */}
            <div className="auth-left">
              <div className="auth-left-content">
                <div className="auth-hero-icon">🎨</div>
                <div className="auth-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <h2>{language === 'zh' ? '欢迎加入 MESAMIND' : 'Welcome to MESAMIND'}</h2>
                <p>{language === 'zh' ? '基于PPC理论的AI艺术教育评估平台，由博物数字美育实验室研发' : 'AI-powered art education platform developed by Bowu Lab'}</p>
                <div className="auth-features">
                  <div className="auth-feature">
                    <div className="auth-feature-icon">✨</div>
                    <span>{language === 'zh' ? '智能画布创作' : 'Smart Canvas'}</span>
                  </div>
                  <div className="auth-feature">
                    <div className="auth-feature-icon">🧠</div>
                    <span>{language === 'zh' ? 'AI素养评估' : 'AI Assessment'}</span>
                  </div>
                  <div className="auth-feature">
                    <div className="auth-feature-icon">📈</div>
                    <span>{language === 'zh' ? '实时数据分析' : 'Real-time Analysis'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="auth-right">
              <div className="auth-tabs">
                <button className="auth-tab active" onClick={() => setIsLogin(true)}>
                  {t.login.loginBtn}
                </button>
                {/* Registration disabled — accounts created by school admin only */}
              </div>

              <form onSubmit={handleSubmit}>
                {/* Role Selection - Only for Signup */}
                {!isLogin && (
                  <div className="role-selector">
                    <label>{t.login.selectRole}</label>
                    <div className="role-options">
                      {['student', 'teacher'].map((role) => (
                        <button
                          key={role}
                          type="button"
                          className={`role-option ${selectedRole === role ? 'active' : ''}`}
                          onClick={() => setSelectedRole(role)}
                        >
                          <span className="role-emoji">
                            {role === 'student' ? '🎨' : '👨‍🏫'}
                          </span>
                          <span>{t.login[role]}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {!isLogin && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t.login.username}</label>
                      <input
                        type="text"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        placeholder={language === 'zh' ? '用户名' : 'Username'}
                        required
                      />
                    </div>
                    {selectedRole === 'student' && (
                      <div className="form-group">
                        <label>{language === 'zh' ? '出生日期' : 'Date of Birth'}</label>
                        <input
                          type="date"
                          value={formData.birthDate || ''}
                          onChange={(e) => {
                            const birthDate = e.target.value;
                            const today = new Date();
                            const birth = new Date(birthDate);
                            let age = today.getFullYear() - birth.getFullYear();
                            const monthDiff = today.getMonth() - birth.getMonth();
                            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
                              age--;
                            }
                            setFormData({ ...formData, birthDate, age: age > 0 ? age : '' });
                          }}
                          max={new Date().toISOString().split('T')[0]}
                          required
                        />
                      </div>
                    )}
                  </div>
                )}

                {isLogin && (
                  <div className="form-group">
                    <label>{t.login.username}</label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      placeholder={language === 'zh' ? '用户名' : 'Username'}
                      required
                    />
                  </div>
                )}

                {!isLogin && (
                  <div className="form-group">
                    <label>{t.login.email}</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder={language === 'zh' ? '邮箱地址' : 'Email'}
                      required
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>{t.login.password}</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={language === 'zh' ? '密码' : 'Password'}
                    required
                  />
                </div>

                {!isLogin && (
                  <div className="form-group">
                    <label>{t.login.confirmPassword}</label>
                    <input
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder={language === 'zh' ? '确认密码' : 'Confirm'}
                      required
                    />
                  </div>
                )}

                {error && (
                  <div className="auth-error">
                    {error}
                  </div>
                )}

                <button type="submit" className="btn-submit" disabled={loading}>
                  {loading
                    ? (language === 'zh' ? '处理中...' : 'Processing...')
                    : (isLogin ? t.login.loginBtn : t.login.signupBtn)
                  }
                </button>

                {isLogin && (
                  <>
                    <button type="button" className="forgot-link" onClick={() => setForgotMsg(language === 'zh' ? '请联系您的老师或管理员重置密码。' : 'Please contact your teacher or administrator to reset your password.')}>{t.login.forgotPassword}</button>
                    {forgotMsg && <p className="forgot-message">{forgotMsg}</p>}
                  </>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-dark">
        <div className="stats-container">
          <div className="stat-card">
            <div className="stat-number">100,000+</div>
            <div className="stat-label">{t.stats.students}</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">100+</div>
            <div className="stat-label">{t.stats.teachers}</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">100,000+</div>
            <div className="stat-label">{t.stats.artworks}</div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-dark" id="features">
        <div className="section-container">
          <div className="section-header">
            <h2>{t.features.title}</h2>
            <p>{t.features.subtitle}</p>
          </div>

          <div className="features-grid">
            {t.features.items.map((feature, index) => (
              <div className="feature-card" key={index}>
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About / How It Works Section */}
      <section className="about-dark" id="about">
        <div className="section-container">
          <div className="about-content">
            <div className="about-text">
              <h2>{t.about.title}</h2>
              <p className="about-subtitle">{t.about.subtitle}</p>
              <p className="about-desc">{t.about.description}</p>

              <div className="streams-container">
                <div className="stream-card">
                  <div className="stream-icon">&#128248;</div>
                  <div className="stream-content">
                    <h4>{t.about.streamA.title}</h4>
                    <p>{t.about.streamA.desc}</p>
                  </div>
                </div>
                <div className="stream-card">
                  <div className="stream-icon">&#9889;</div>
                  <div className="stream-content">
                    <h4>{t.about.streamB.title}</h4>
                    <p>{t.about.streamB.desc}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="about-visual">
              <div className="ppc-diagram">
                <div className="diagram-center">
                  <span>PPC</span>
                </div>
                <div className="diagram-orbit">
                  <div className="orbit-item item-1">{language === 'zh' ? '过程' : 'Process'}</div>
                  <div className="orbit-item item-2">{language === 'zh' ? '作品' : 'Product'}</div>
                  <div className="orbit-item item-3">{language === 'zh' ? '一致性' : 'Consistency'}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="roles-section">
            <h3>{t.about.roles.title}</h3>
            <div className="roles-grid">
              <div className="role-card role-student">
                <div className="role-icon">&#127912;</div>
                <h4>{t.about.roles.student.title}</h4>
                <p>{t.about.roles.student.desc}</p>
              </div>
              <div className="role-card role-teacher">
                <div className="role-icon">&#128104;&#8205;&#127979;</div>
                <h4>{t.about.roles.teacher.title}</h4>
                <p>{t.about.roles.teacher.desc}</p>
              </div>
              <div className="role-card role-admin">
                <div className="role-icon">&#9881;</div>
                <h4>{t.about.roles.admin.title}</h4>
                <p>{t.about.roles.admin.desc}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-dark" id="pricing">
        <div className="cta-container">
          <div className="cta-glow"></div>
          <h2>{t.cta.title}</h2>
          <p>{t.cta.subtitle}</p>
          <button className="btn-cta" onClick={() => navigate('/canvas')}>
            {t.cta.button}
          </button>
          <span className="cta-note">{t.cta.noCreditCard}</span>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-dark">
        <div className="footer-container">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <span className="logo-icon">&#9670;</span>
                <span>MESAMIND</span>
              </div>
              <p>{language === 'zh' ? 'AI驱动的美术教育智能评估平台' : 'AI-Powered Art Education Assessment Platform'}</p>
            </div>

          </div>

          <div className="footer-bottom">
            <p>{t.footer.copyright}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
